import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { HandleUserSelectionProps } from '@nykaa/beauty-portfolio/types';
import { ChangeAccordion } from '@nykaa/ui-kit/component/Accordion/types';
import { Question } from '@nykaa/beauty-portfolio-shared/store/types';
import {
  hasLocalStateChanged,
  hasQuestionsResponseChanged,
} from '@nykaa/beauty-portfolio-shared/utils';
import {
  ConsentFilledState,
  QuestionFilledState,
} from '@nykaa/beauty-portfolio-shared/types';
import { AB_BP_DN_PERS } from 'constants/experiments';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import { getEligibleForVariant } from 'helpers/experimentV2';

export const usePrevious = <T>(value?: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export const useCompareState = (
  currentState: QuestionFilledState | ConsentFilledState
): boolean => {
  const prevState = usePrevious(currentState) || {};
  return hasLocalStateChanged(prevState, currentState);
};

interface UserSelectionProps {
  currentActiveIndex?: number;
  questions?: Question[];
  userFilledState: QuestionFilledState;
  setUserFilledState: Dispatch<SetStateAction<QuestionFilledState>>;
  setActiveId?: ChangeAccordion;
}

export const useUserSelection = ({
  currentActiveIndex = 0,
  questions = [],
  userFilledState,
  setUserFilledState,
  setActiveId,
}: UserSelectionProps) => {
  const hasResponseChange = hasQuestionsResponseChanged(
    userFilledState,
    questions
  );
  const handleUserSelection = useCallback(
    ({
      quesId,
      optId = '',
      multiSelect,
      dob: dateOfBirth = '',
      optionLimit,
    }: HandleUserSelectionProps) => {
      setUserFilledState((prevSelections) => {
        const { selectedOptions = [], dob = '' } = prevSelections[quesId] || {};

        if (selectedOptions.includes(optId) && multiSelect) {
          const updatedSelectedOptions = selectedOptions.filter(
            (id) => id !== optId
          );
          return {
            ...prevSelections,
            [quesId]: {
              ...prevSelections[quesId],
              selectedOptions: updatedSelectedOptions,
            },
          };
        }

        const updatedSelectedOptions = multiSelect
          ? [...selectedOptions, optId]
          : [optId];

        const isLastOptionSelected =
          currentActiveIndex !== -1 &&
          currentActiveIndex < questions.length - 1 &&
          updatedSelectedOptions.length === optionLimit;

        if (isLastOptionSelected) {
          const nextIndex = currentActiveIndex + 1;
          const nextQuestionId = questions[nextIndex].id;
          setActiveId && setActiveId(nextQuestionId);
        }

        return {
          ...prevSelections,
          [quesId]: {
            ...prevSelections[quesId],
            selectedOptions: updatedSelectedOptions,
            ...(dateOfBirth ? { dob: dateOfBirth || dob } : {}),
          },
        };
      });
    },
    [currentActiveIndex, questions, setActiveId, setUserFilledState]
  );

  return { hasResponseChange, userFilledState, handleUserSelection };
};

export const useCurrentActiveIndex = (
  questions: Question[],
  activeId: string
) =>
  useMemo(
    () => questions.findIndex((question) => question.id === activeId),
    [questions, activeId]
  );

export const useIsUserSegmentEnabled = (): boolean =>
  useSelector((state: State) =>
    getEligibleForVariant(state, AB_BP_DN_PERS, 'A')
  );
